// #region Imports
import { defineComponent, defineAsyncComponent } from "vue";
// #endregion
// #region Core
export default defineComponent({
    // #region Vars
    name: 'g-file-field',
    // #endregion
    // #region Events
    emits: ['uploaded', 'deleted'],
    // #endregion
    // #region Mixins
    mixins: [appHost.mixins.form],
    // #endregion
    // #region Components
    components: {
        DxFileUploader: defineAsyncComponent(async () => {
            return (await import(/* webpackChunkName: "dxCommon" */ /* webpackMode: "lazy" */ 'devextreme-vue/file-uploader')).DxFileUploader;
        }),
        DxProgressBar: defineAsyncComponent(async () => {
            return (await import(/* webpackChunkName: "dxCommon" */ /* webpackMode: "lazy" */ 'devextreme-vue/progress-bar')).DxProgressBar;
        }),
    },
    // #endregion
    // #region Props
    props: {
        accept: {
            type: String,
            required: false,
            default: '*',
        },
        multiple: {
            type: Boolean,
            required: false,
            default: true,
        },
        maxFileSize: {
            type: Number,
            required: false,
            default: 0,
        },
        chunkSize: {
            type: Number,
            required: false,
            default: 1000000,
        },
        inputAttr: {
            type: Object,
            required: false,
            default: null,
        },
        uploadUrl: {
            type: String,
            required: false,
            default: null,
        },
        openUrl: {
            type: String,
            required: false,
            default: null,
        },
        deleteUrl: {
            type: String,
            required: false,
            default: null,
        },
        emptyText: {
            type: String,
            required: false,
            default: null,
        },
        readOnlyText: {
            type: String,
            required: false,
            default: null,
        },
        customFields: {
            type: Object,
            required: false,
            default: null,
        },
    },
    data: () => ({
        _elementID: utils.createElementId(),
        _errorMessage: '',
        _uploadProgress: 0,
        _uploadProgressVisible: false,
        _isDropZoneActive: false,
        _uploadedFiles: [],
    }),
    computed: {
        allowedFileExtensions() {
            //Mew
            let tNew = [];
            //Accept
            if (!this.accept) {
                return tNew;
            }
            //Data
            this.accept.split(',').forEach(e => {
                if (e.startsWith(".")) {
                    tNew.push(e);
                }
            });
            return tNew;
        },
        inputAttrInternal() {
            return this.inputAttr;
        },
        _files() {
            return this.internalValue;
        },
    },
    // #endregion
    // #region Methods
    methods: {
        // #region Uploader
        onDropZoneEnter(e) {
            if (e.dropZoneElement.id === this._elementID) {
                this._isDropZoneActive = true;
            }
        },
        onDropZoneLeave(e) {
            if (e.dropZoneElement.id === this._elementID) {
                this._isDropZoneActive = false;
            }
        },
        onUploadStarted() {
            this._errorMessage = '';
            this._uploadProgressVisible = true;
            this._uploadedFiles = [];
        },
        onProgress(e) {
            this._uploadProgress = (e.bytesLoaded / e.bytesTotal) * 100;
        },
        onUploaded(e) {
            //Reset
            e.component.reset();
            //Event
            this.$emit('uploaded', { files: this._files });
        },
        onUploadedFile(e) {
            //Vars
            let tFile = e.file;
            //Vars
            this._isDropZoneActive = false;
            this._uploadProgressVisible = false;
            this._uploadProgress = 0;
            //Data
            let tNew = {
                name: tFile.name,
                uploadedAt: new Date(Date.now()).toJSON(),
                fileSize: tFile.size,
                fileChangedAt: new Date(tFile.lastModified).toJSON(),
                fileID: e.request.getResponseHeader("X-File-Guid"),
                fileName: e.request.getResponseHeader("X-File-Name"),
                fileUrl: e.request.getResponseHeader("X-File-Url"),
            };
            //CustomFields
            if (this.customFields) {
                Object.keys(this.customFields).forEach(e => {
                    tNew[e] = this.customFields[e];
                });
            }
            //Internal
            if (!Array.isArray(this.internalValue)) {
                this.internalValue = [];
            }
            //Data
            this.internalValue.push(tNew);
        },
        onUploadedError(e) {
            this._errorMessage = e.message;
            this._isDropZoneActive = false;
            this._uploadProgressVisible = false;
            this._uploadProgress = 0;
        },
        onValueChanged(e) {
            if (e.value && utils.isArray(e.value)) {
                e.value.forEach(tFile => {
                    if (this.maxFileSize > 0 && tFile && tFile.size > this.maxFileSize) {
                        let tNew = {
                            name: tFile.name,
                            uploadedAt: new Date(Date.now()).toJSON(),
                            message: this.$refs?.fileUploader?.invalidMaxFileSizeMessage ?? this.$t('client.Forms.File.ToBig'),
                            fileSize: tFile.size,
                        };
                        //Internal
                        if (!Array.isArray(this.internalValue)) {
                            this.internalValue = [];
                        }
                        //Data
                        this.internalValue.push(tNew);
                    }
                });
            }
        },
        // #endregion
        // #region CRUD
        onDeleteFile(file) {
            // #region Reset
            this._errorMessage = '';
            // #endregion
            // #region Run
            if (this.deleteUrl) {
                let tUrl = this.$utils.evalParams(this.deleteUrl, file);
                if (tUrl) {
                    let tComp = this;
                    let xhr = new XMLHttpRequest();
                    let tMessage = this.$t('client.Forms.File.ErrorDelete');
                    //Data
                    xhr.open("DELETE", this.$utils.evalParams(this.deleteUrl, file), true);
                    //Events
                    xhr.onload = function () {
                        if (this.status >= 200 && this.status < 300) {
                            tComp._files.splice(tComp._files.indexOf(file), 1);
                            tComp.$emit('deleted', { files: [file] });
                        }
                        else {
                            tComp._errorMessage = xhr.statusText ? xhr.statusText : tMessage;
                        }
                    };
                    xhr.onerror = function () {
                        tComp._errorMessage = xhr.statusText;
                    };
                    //Send
                    xhr.send();
                }
                else {
                    this._files.splice(this._files.indexOf(file), 1);
                    this.$emit('deleted', { files: [file] });
                }
            }
            // #endregion
            // #region Default
            else {
                // #region Remove
                this._files.splice(this._files.indexOf(file), 1);
                this.$emit('deleted', { files: [file] });
                // #endregion
            }
            // #endregion
        }
        // #endregion
    }
    // #endregion
});
// #endregion
